import React, { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, mediaUrl } from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PostCard from "../components/PostCard";
import AdSlot from "../components/AdSlot";
import FloatingSidebarAd from "../components/FloatingSidebarAd";
import { Share2, ChevronRight, ShieldCheck, Instagram, ChevronDown, Globe } from "lucide-react";
import WhatsAppIcon from "../components/WhatsAppIcon";
import AuthorByline from "../components/AuthorByline";
import NewsletterSignup from "../components/NewsletterSignup";
import { useLang, LANGS } from "../lib/LangContext";

import { WHATSAPP_URL, INSTAGRAM_URL } from "../lib/constants";

const TEAM_PHOTO = "https://customer-assets-39nsmqrw.emergentagent.net/job_orion-content-lab/artifacts/bc7ompfx_equipe%20orion%20-%20Copia.jpeg";

export default function Article() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { lang, setLang } = useLang();
  const [data, setData] = useState(null);
  const [author, setAuthor] = useState(null);
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    // Public on-demand translation: pass ?lang=xx so backend translates & caches
    const q = lang && lang !== "pt" ? `?lang=${lang}` : "";
    api.get(`/posts/slug/${slug}${q}`).then(async (r) => {
      // Legacy slug: the article has moved to a new URL after a SEO rewrite.
      // Replace-navigate so back button doesn't ping-pong and Google reads
      // the response as a soft redirect to the canonical slug.
      if (r.data.redirect_to && r.data.redirect_to !== slug) {
        navigate(`/artigo/${r.data.redirect_to}`, { replace: true });
        return;
      }
      setData(r.data);
      document.title = `${r.data.post.meta_title || r.data.post.title} — Viagens Orion`;
      if (typeof window.__trackPageView === "function") {
        window.__trackPageView(`/artigo/${r.data.post.slug}`, document.title);
      }

      const setOrCreate = (selector, attrs) => {
        let el = document.querySelector(selector);
        if (!el) {
          el = document.createElement(selector.startsWith("meta") ? "meta" : "link");
          document.head.appendChild(el);
        }
        Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      };

      const post = r.data.post;
      const desc = post.meta_description || post.excerpt || "";
      const _bustOg = (post.updated_at || post.published_at || "").replace(/[^0-9]/g, "").slice(-10);
      const coverAbs = post.cover_image_id
        ? `${window.location.origin}/api/media/${post.cover_image_id}/desktop${_bustOg ? `?v=${_bustOg}` : ""}`
        : "https://customer-assets-39nsmqrw.emergentagent.net/job_orion-content-lab/artifacts/4e2ljpee_blog%20da%20fada%20icon.jpeg";
      const canonical = `${window.location.origin}/artigo/${post.slug}`;

      setOrCreate('meta[name="description"]', { name: "description", content: desc });
      if (post.focus_keyword)
        setOrCreate('meta[name="keywords"]', { name: "keywords", content: post.focus_keyword });
      setOrCreate('link[rel="canonical"]', { rel: "canonical", href: canonical });
      setOrCreate('meta[property="og:type"]', { property: "og:type", content: "article" });
      setOrCreate('meta[property="og:title"]', { property: "og:title", content: post.meta_title || post.title });
      setOrCreate('meta[property="og:description"]', { property: "og:description", content: desc });
      setOrCreate('meta[property="og:image"]', { property: "og:image", content: coverAbs });
      setOrCreate('meta[property="og:url"]', { property: "og:url", content: canonical });
      setOrCreate('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
      setOrCreate('meta[name="twitter:title"]', { name: "twitter:title", content: post.meta_title || post.title });
      setOrCreate('meta[name="twitter:description"]', { name: "twitter:description", content: desc });
      setOrCreate('meta[name="twitter:image"]', { name: "twitter:image", content: coverAbs });

      // JSON-LD Article
      let ld = document.getElementById("article-jsonld");
      if (!ld) {
        ld = document.createElement("script");
        ld.type = "application/ld+json";
        ld.id = "article-jsonld";
        document.head.appendChild(ld);
      }
      ld.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Article",
        headline: post.title,
        description: desc,
        image: coverAbs,
        datePublished: post.published_at,
        dateModified: post.updated_at,
        author: {
          "@type": "Person",
          name: post.author || "Equipe Viagens Orion",
        },
        publisher: {
          "@type": "Organization",
          name: "Viagens Orion",
          logo: {
            "@type": "ImageObject",
            url: `${window.location.origin}/img/orion-logo.png`,
          },
        },
        mainEntityOfPage: canonical,
        keywords: post.focus_keyword,
      });

      const slugAuthor = post.author_slug || "equipe-viagens-orion";
      try {
        const a = await api.get(`/authors/${slugAuthor}`);
        setAuthor(a.data.author);
      } catch { setAuthor(null); }
      try {
        const t = await api.get(`/authors/equipe-viagens-orion`);
        setTeam(t.data.author);
      } catch { setTeam(null); }
    }).catch(() => setNotFound(true))
      .finally(() => setLoading(false));

    return () => {
      const ld = document.getElementById("article-jsonld");
      if (ld) ld.remove();
    };
  }, [slug, lang]);

  if (notFound) return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="container-orion py-32 text-center flex-1">
        <div className="cat-chip mb-4">404</div>
        <h1 className="font-serif text-4xl text-orion-purple mb-4">Artigo não encontrado</h1>
        <Link to="/" className="text-orion-orange uppercase tracking-widest text-xs font-semibold">Voltar para a home</Link>
      </main>
      <Footer />
    </div>
  );

  if (loading || !data) return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <div className="container-orion py-32 text-center text-[#8A8191] flex-1">Carregando…</div>
      <Footer />
    </div>
  );

  const { post, related } = data;
  // Cache-buster: hash curto derivado do updated_at (ou published_at). Força
  // o browser e Cloudflare a re-baixar a capa quando o admin troca a imagem
  // no painel — mesmo que o cover_image_id continue igual, o hash muda.
  const _ts = post.updated_at || post.published_at || "";
  const _bust = _ts ? _ts.replace(/[^0-9]/g, "").slice(-10) : "";
  const _cid = post.cover_image_id;
  const cover = _cid
    ? `${mediaUrl(_cid, "desktop")}${_bust ? `?v=${_bust}` : ""}`
    : post.cover_url;
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  // DeepL returns HTML-encoded entities (e.g. Rio&#x27;s) since we translate
  // with tag_handling=html. Decode entities for plain-text fields (title, subtitle).
  const decodeEntities = (s) => {
    if (!s || typeof document === "undefined") return s;
    const ta = document.createElement("textarea");
    ta.innerHTML = s;
    return ta.value;
  };

  // Localized rendering: backend overlays translated fields when ?lang=xx is
  // present, so we render post fields directly. The "_lang" marker on the
  // response tells us the translation succeeded (green notice) vs. fallback
  // to Portuguese (red missing notice).
  const translationApplied = !!post._lang && post._lang === lang;
  const isRtl = !!post._rtl;
  const displayTitle = decodeEntities(post.title);
  const displaySubtitle = decodeEntities(post.subtitle);
  const displayContent = post.content;
  const showTranslationNotice = lang !== "pt";
  const NOTICE_COPY = {
    en: { translated: "Auto-translated with DeepL. Read original in Portuguese",       missing: "This article isn't translated to English yet — showing Portuguese",       switch: "Switch" },
    es: { translated: "Traducido automáticamente con DeepL. Leer original en portugués",missing: "Este artículo aún no fue traducido al español — mostrando portugués",       switch: "Cambiar" },
    ar: { translated: "تُرجم تلقائيًا بواسطة DeepL. اقرأ الأصل بالبرتغالية",              missing: "لم تتم ترجمة هذه المقالة إلى العربية بعد — يتم عرضها بالبرتغالية",           switch: "تبديل" },
    ja: { translated: "DeepLにより自動翻訳。ポルトガル語の原文を読む",                     missing: "この記事はまだ日本語に翻訳されていません — ポルトガル語で表示",              switch: "切替" },
    ko: { translated: "DeepL 자동 번역. 포르투갈어 원문 보기",                             missing: "이 문서는 아직 한국어로 번역되지 않았습니다 — 포르투갈어로 표시",             switch: "전환" },
    zh: { translated: "已由 DeepL 自动翻译。阅读葡萄牙语原文",                              missing: "本文尚未翻译成中文 — 显示葡萄牙语",                                          switch: "切换" },
  };

  // Use the backend /api/share/{slug} endpoint for WhatsApp/Facebook so the
  // scrapers pick up the per-article Open Graph tags (cover image + title).
  // Real users clicking the shared link are auto-redirected to the SPA article.
  const shareUrl = `${origin}/api/share/${post.slug}`;
  const articleUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareText = `${displayTitle} — Viagens Orion`;

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <article className="flex-1" data-testid="article-page" dir={isRtl ? "rtl" : "ltr"}>
        {/* Breadcrumb */}
        <div className="container-orion pt-8 text-xs uppercase tracking-[0.2em] text-[#8A8191] flex items-center gap-2">
          <Link to="/" className="hover:text-orion-orange">Início</Link>
          <ChevronRight size={12} />
          {post.categories?.[0] && <><span className="text-orion-orange">{post.categories[0]}</span></>}
        </div>

        {/* Title */}
        <header className="container-orion pt-8 pb-10 max-w-4xl">
          {showTranslationNotice && (
            <div
              className="mb-6 inline-flex items-center gap-3 text-[10px] uppercase tracking-[0.24em] font-semibold text-orion-purple/80 bg-orion-cream border border-orion-gold/30 px-3 py-2"
              data-testid="translation-notice"
            >
              <span className="text-orion-gold">{lang.toUpperCase()}</span>
              <span className="normal-case tracking-normal text-[11px] text-[#594A63]">
                {translationApplied ? NOTICE_COPY[lang]?.translated : NOTICE_COPY[lang]?.missing}
              </span>
              <button
                type="button"
                onClick={() => setLang("pt")}
                className="uppercase tracking-[0.22em] text-orion-orange hover:underline"
                data-testid="translation-notice-back-pt"
              >
                {NOTICE_COPY[lang]?.switch} PT
              </button>
            </div>
          )}
          <h1 className="font-serif text-[1.7rem] sm:text-3xl md:text-5xl lg:text-6xl font-semibold text-orion-purple leading-[1.1]" data-testid="article-title">
            {displayTitle}
          </h1>
          {displaySubtitle && (
            <p className="mt-4 md:mt-6 text-base md:text-2xl font-serif italic text-[#594A63] leading-snug max-w-3xl">
              {displaySubtitle}
            </p>
          )}
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <AuthorByline
              author={author}
              authorSlug={post.author_slug}
              size="md"
              dateLabel={post.published_at ? new Date(post.published_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" }) : null}
            />
            {/* Language dropdown — mostra a bandeira do idioma atual (🇧🇷 por padrão).
                Ao clicar, abre a lista com os 6 idiomas de tradução. Assim brasileiros
                não clicam à toa; quem precisa trocar, encontra sem esforço. */}
            <LangDropdown />
          </div>
        </header>

        {/* Cover */}
        {cover && (
          <div className="container-orion mb-14">
            <img
              src={cover}
              alt={(post.focus_keyword ? `${post.focus_keyword} — ` : "") + post.title + (post.subtitle ? ` — ${post.subtitle.slice(0, 80)}` : "")}
              loading="eager"
              fetchPriority="high"
              decoding="async"
              width="1600"
              height="900"
              className="w-full aspect-video object-cover"
              data-testid="article-cover"
            />
          </div>
        )}

        <AdSlot id="article-top" label="Publicidade · Google AdSense" height={90} />

        {/* Body */}
        <div className="container-orion">
          <div className="mx-auto max-w-reading prose-orion" dangerouslySetInnerHTML={{ __html: displayContent }} data-testid="article-content" />
        </div>

        {/* Middle ad — mesmo slot 'home-mid' do combo horizontal, aparece
            também dentro dos artigos para o anúncio do meio ficar consistente
            em todas as páginas (não só na home). */}
        <AdSlot id="home-mid" label="Publicidade · Google AdSense" height={90} />

        {/* Sidebar flutuante 300×600 (half-page IAB) — sticky no lado direito
            em telas ≥ 1280px. Escondido totalmente em mobile/tablet para não
            competir com o conteúdo. Aparece só se houver um anúncio ativo. */}
        <FloatingSidebarAd />

        {/* Share */}
        <div className="container-orion mt-14 max-w-reading mx-auto">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-xs uppercase tracking-widest text-[#8A8191]">
            <span className="inline-flex items-center gap-2"><Share2 size={14} /> Compartilhar:</span>
            <a target="_blank" rel="noreferrer" href={`https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}`} className="hover:text-orion-orange inline-flex items-center gap-1.5" data-testid="share-whatsapp">
              <WhatsAppIcon size={12} color="#25D366" /> WhatsApp
            </a>
            <a target="_blank" rel="noreferrer" href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`} className="hover:text-orion-orange" data-testid="share-facebook">Facebook</a>
            <a target="_blank" rel="noreferrer" href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`} className="hover:text-orion-orange" data-testid="share-twitter">Twitter</a>
            <button
              type="button"
              onClick={() => {
                if (navigator.clipboard) {
                  // shareUrl points to /api/share/{slug} which serves SSR
                  // Open Graph meta so WhatsApp/Facebook show the article's
                  // real cover image — real users hitting it are auto-
                  // redirected to /artigo/{slug} via meta refresh.
                  navigator.clipboard.writeText(shareUrl)
                    .then(() => toast.success("Link copiado!", {
                      description: "Este é o link certo — o preview vai mostrar a capa deste artigo.",
                      duration: 3200,
                    }))
                    .catch(() => toast.error("Não foi possível copiar o link"));
                }
              }}
              className="hover:text-orion-orange"
              data-testid="share-copy-link"
              aria-label="Copiar link do artigo com preview correto"
            >
              Copiar link para preview
            </button>
          </div>
          {/* Native primary CTA — highlights WhatsApp share (biggest social channel for Orion) */}
          <a
            target="_blank"
            rel="noreferrer"
            href={`https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}`}
            className="mt-4 inline-flex items-center gap-2 bg-[#25D366] text-white px-5 py-2.5 text-[11px] uppercase tracking-widest hover:bg-[#1EBE57] transition-colors shadow-[0_6px_18px_rgba(37,211,102,0.25)]"
            data-testid="share-whatsapp-cta"
          >
            <WhatsAppIcon size={14} color="#ffffff" /> Compartilhar no WhatsApp
          </a>
        </div>

        {/* CTA Agency */}
        <section className="container-orion mt-16 max-w-reading mx-auto bg-orion-purple text-white p-10 md:p-14" data-testid="agency-cta">
          <div className="cat-chip !text-orion-gold mb-3">Quer viver essa viagem?</div>
          <h3 className="font-serif text-xl md:text-3xl leading-tight">Fale com a Viagens Orion e receba um roteiro sob medida.</h3>
          <p className="mt-4 text-white/80 text-sm max-w-md">Use o cupom <span className="text-orion-orange font-semibold tracking-wider">TURISTAR</span> e ganhe 10% de desconto na sua reserva. Nossa equipe monta o pacote, cuida das reservas e acompanha você antes, durante e depois da viagem.</p>
          <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="mt-6 inline-flex items-center gap-2 bg-[#25D366] px-6 py-3 text-xs uppercase tracking-widest hover:bg-[#1EBE57] transition-colors shadow-[0_10px_24px_rgba(37,211,102,0.35)]" data-testid="agency-cta-btn">
            <WhatsAppIcon size={14} color="#ffffff" /> Falar no WhatsApp
          </a>
        </section>

        {/* article-bottom removido conforme feedback do editor — o AdSense
            não exige esse slot, e a sidebar flutuante 300×600 (xl+) já cobre
            impressões abaixo do fold sem quebrar o fluxo de leitura. */}
        {/* NOTE: sidebar slot removed from inline article bottom.
            The article layout is single-column (`max-w-reading`), so a
            300×600 half-page rendered inline created a giant empty
            block that hurt content visibility. Sidebar ads should be
            served in a real lateral column — pending layout revamp. */}

        {/* Assinatura do Nosso Time — sempre mostra a Equipe Viagens Orion,
            independentemente de quem assinou o post no byline. Individualizado
            só quando houver foto exclusiva do autor (feature futura). */}
        <section
          className="container-orion mt-16 max-w-3xl mx-auto"
          data-testid="team-author-section"
          aria-label="Nosso Time · Viagens Orion"
        >
          <div className="team-photo-frame text-white overflow-hidden rounded-2xl">
            <div className="relative z-[1] grid md:grid-cols-[280px,1fr] gap-0">
              {/* Photo — team group photo, rectangular w/ rounded corners */}
              <div className="relative p-6 md:p-8 flex items-center justify-center">
                <div className="relative w-full">
                  <span className="absolute -inset-3 rounded-2xl bg-orion-orange/25 blur-2xl" aria-hidden />
                  <span className="absolute -inset-[3px] rounded-2xl bg-gradient-to-br from-orion-gold via-orion-orange to-orion-gold opacity-80" aria-hidden />
                  <img
                    src={team?.photo_url || TEAM_PHOTO}
                    alt="Equipe Viagens Orion"
                    loading="lazy"
                    decoding="async"
                    width="600"
                    height="400"
                    className="relative h-56 md:h-64 w-full object-cover rounded-2xl ring-4 ring-white/20 shadow-[0_18px_50px_rgba(0,0,0,0.45)]"
                    style={{ objectPosition: team?.photo_position || "center" }}
                    data-testid="team-photo"
                  />
                  <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-white text-orion-purple text-[10px] uppercase tracking-[0.28em] font-semibold px-3 py-1 rounded-full shadow whitespace-nowrap">
                    Assinado
                  </span>
                </div>
              </div>

              {/* Text — always team-focused */}
              <div className="p-6 md:p-10 md:pl-4 flex flex-col justify-center">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] text-orion-gold font-semibold">
                  Assinado pelo
                </div>
                <h3 className="mt-2 font-serif text-2xl md:text-4xl leading-tight" data-testid="team-block-name">
                  Nosso Time
                </h3>
                <p className="mt-3 text-sm md:text-base text-white/80 leading-relaxed max-w-md">
                  {team?.bio || "Consultores de viagem que já passaram por dezenas de destinos: montamos os roteiros que publicamos aqui, cuidamos das reservas e acompanhamos você antes, durante e depois da viagem."}
                </p>
                <div className="mt-5 flex flex-wrap gap-3">
                  <a
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 bg-[#25D366] text-white px-5 py-2.5 text-[11px] uppercase tracking-[0.22em] font-semibold hover:bg-[#1EBE57] transition-colors shadow-[0_10px_24px_rgba(37,211,102,0.35)]"
                    data-testid="team-whatsapp-btn"
                  >
                    <WhatsAppIcon size={14} color="#ffffff" /> Falar com a equipe
                  </a>
                  <a
                    href={team?.instagram || INSTAGRAM_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 border border-white/25 text-white px-5 py-2.5 text-[11px] uppercase tracking-[0.22em] font-semibold hover:border-orion-orange hover:text-orion-orange transition-colors"
                    data-testid="team-instagram-btn"
                  >
                    <Instagram size={14} /> @viagensorion
                  </a>
                </div>
                <div className="mt-5 flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-white/55">
                  <ShieldCheck size={12} className="text-orion-gold" /> Cadastur · CNPJ 37.476.039/0001-90
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Newsletter block */}
        <div className="container-orion mt-16 max-w-3xl mx-auto">
          <NewsletterSignup variant="editorial" />
        </div>

        {/* Related */}
        {related?.length > 0 && (
          <section className="container-orion py-16">
            <div className="cat-chip mb-6">Leia também</div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-10">
              {related.map((p) => <PostCard key={p.id} post={p} />)}
            </div>
          </section>
        )}
      </article>

      <Footer />
    </div>
  );
}

/**
 * LangDropdown — botão único com a bandeira do idioma atual (padrão 🇧🇷 Brasil).
 * Ao clicar, abre uma lista compacta com os 6 idiomas de tradução (EN, ES, AR,
 * JA, KO, ZH). Fecha no clique fora ou ESC. Estratégia deliberada: brasileiros
 * (maioria do tráfego) vêem só a bandeira BR e não são tentados a clicar; quem
 * é estrangeiro reconhece o pattern global de "clicar na bandeira para trocar".
 */
function LangDropdown() {
  const { lang, setLang, LANGS: Langs } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = (Langs || LANGS).find((l) => l.code === lang) || (Langs || LANGS)[0];

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative" data-testid="article-lang-dropdown">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="inline-flex items-center gap-2 border border-[#DFD8C7] hover:border-orion-purple hover:text-orion-purple px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] text-[#594A63] transition-colors"
        title="Traduzir este artigo"
        data-testid="lang-dropdown-toggle"
      >
        <span aria-hidden className="text-base leading-none">{current.flag}</span>
        <span className="font-semibold">{current.label}</span>
        <ChevronDown size={13} className={"transition-transform " + (open ? "rotate-180" : "")} />
      </button>
      {open && (
        <ul
          role="listbox"
          className="absolute z-30 mt-1 min-w-[190px] bg-white border border-[#DFD8C7] shadow-[0_18px_40px_rgba(0,0,0,0.15)] py-1"
          data-testid="lang-dropdown-menu"
        >
          <li className="px-3 py-1 text-[9px] uppercase tracking-[0.24em] text-[#8A8191] flex items-center gap-1.5">
            <Globe size={11} /> Traduzir artigo
          </li>
          {(Langs || LANGS).map((L) => {
            const active = lang === L.code;
            return (
              <li key={L.code}>
                <button
                  onClick={() => {
                    setLang(L.code);
                    setOpen(false);
                  }}
                  className={
                    "w-full text-left px-3 py-2 text-xs flex items-center gap-2 transition-colors " +
                    (active
                      ? "bg-orion-cream text-orion-purple font-semibold"
                      : "text-[#594A63] hover:bg-orion-cream/60 hover:text-orion-purple")
                  }
                  data-testid={`lang-option-${L.code}`}
                  role="option"
                  aria-selected={active}
                >
                  <span aria-hidden className="text-base leading-none w-5">{L.flag}</span>
                  <span className="uppercase tracking-widest text-[10px] w-6">{L.label}</span>
                  <span className="text-[11px]">{L.full}</span>
                  {active && <span className="ml-auto text-orion-orange text-[10px]">●</span>}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
